import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Target,
  Zap,
  Plus,
  X,
  Play,
  Pause,
  Trash2,
  GripVertical,
  Brain,
  Shield,
  Server,
  Lock,
  Globe,
  Database,
  Terminal,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Sparkles,
  ArrowRight,
  Settings,
  RefreshCw,
  Monitor,
  Network,
  Key,
  Search,
  Eye,
  Lightbulb,
  TrendingUp,
  Clock,
  Building2,
  Wifi,
  Cloud,
  Router,
  Radio,
  Cpu,
  HardDrive,
  FileSearch,
  Bug,
  Crosshair
} from 'lucide-react';
import { scans, dynamicOrchestrator, intelligence, workspaces as workspacesApi } from '../services/api/client';
import { apiClient } from '../api/client';
import { useI18n } from '../i18n';

// Mapping icon names to components
const ICON_MAP = {
  Network, Shield, Globe, Search, Server, Database, Terminal, Key, Lock, Zap,
  Wifi, Cloud, Router, Radio, Cpu, HardDrive, FileSearch, Bug, Crosshair, Eye, Brain
};

// Helper to get icon by category
const getCategoryIcon = (category) => {
  const iconMap = {
    reconnaissance: Search,
    web: Globe,
    cms: Globe,
    vulnerabilities: Shield,
    bruteforce: Key,
    ad: Server,
    services: Database,
    osint: Eye,
    suites: Terminal,
    ssl: Lock,
    ot: Cpu,
    iot: Radio,
    pivoting: Network,
    privesc: TrendingUp,
    evasion: Bug,
    supply_chain: HardDrive,
    network_attack: Crosshair,
    firewall: Router,
    vpn: Wifi,
    cloud: Cloud
  };
  return iconMap[category] || Zap;
};

// Colors by category
const getCategoryColor = (category) => {
  const colorMap = {
    reconnaissance: 'blue',
    web: 'cyan',
    cms: 'teal',
    vulnerabilities: 'orange',
    bruteforce: 'red',
    ad: 'purple',
    services: 'indigo',
    osint: 'emerald',
    suites: 'violet',
    ssl: 'amber',
    ot: 'rose',
    iot: 'pink',
    pivoting: 'lime',
    privesc: 'yellow',
    evasion: 'fuchsia',
    supply_chain: 'sky',
    network_attack: 'red',
    firewall: 'orange',
    vpn: 'green',
    cloud: 'blue'
  };
  return colorMap[category] || 'gray';
};

// Category names
const categoryNames = {
  reconnaissance: 'Reconnaissance',
  web: 'Scan Web',
  cms: 'Scanners CMS',
  vulnerabilities: 'Vulnerability scanners',
  bruteforce: 'Force brute',
  ad: 'Active Directory',
  services: 'Service enumeration',
  osint: 'Renseignement en sources ouvertes (OSINT)',
  suites: 'Security suites',
  ssl: 'SSL/TLS',
  ot: 'OT/ICS/SCADA',
  iot: 'Appareils IoT',
  pivoting: 'Pivotement et tunneling',
  privesc: 'Privilege escalation',
  evasion: 'Evasion and bypass',
  supply_chain: 'Supply chain security',
  network_attack: 'Network attacks',
  firewall: 'Box / Pare-feu / Routeur',
  vpn: 'VPN security',
  cloud: 'Cloud security'
};

// Custom Tooltip component
const Tooltip = ({ children, content }) => {
  const [show, setShow] = useState(false);

  return (
    <div className="relative inline-block" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && content && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg shadow-xl text-xs text-gray-200 whitespace-normal min-w-[200px] max-w-[300px]">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-700"></div>
        </div>
      )}
    </div>
  );
};

// Composant pour un module draggable avec tooltip
const DraggableModule = ({ module, category, onDragStart, disabled, onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const color = getCategoryColor(category);
  const colorClasses = {
    blue: 'bg-blue-500/20 border-blue-500/40 hover:border-blue-400',
    cyan: 'bg-cyan-500/20 border-cyan-500/40 hover:border-cyan-400',
    teal: 'bg-teal-500/20 border-teal-500/40 hover:border-teal-400',
    orange: 'bg-orange-500/20 border-orange-500/40 hover:border-orange-400',
    red: 'bg-red-500/20 border-red-500/40 hover:border-red-400',
    purple: 'bg-purple-500/20 border-purple-500/40 hover:border-purple-400',
    indigo: 'bg-indigo-500/20 border-indigo-500/40 hover:border-indigo-400',
    emerald: 'bg-emerald-500/20 border-emerald-500/40 hover:border-emerald-400',
    violet: 'bg-violet-500/20 border-violet-500/40 hover:border-violet-400',
    amber: 'bg-amber-500/20 border-amber-500/40 hover:border-amber-400',
    rose: 'bg-rose-500/20 border-rose-500/40 hover:border-rose-400',
    pink: 'bg-pink-500/20 border-pink-500/40 hover:border-pink-400',
    lime: 'bg-lime-500/20 border-lime-500/40 hover:border-lime-400',
    yellow: 'bg-yellow-500/20 border-yellow-500/40 hover:border-yellow-400',
    fuchsia: 'bg-fuchsia-500/20 border-fuchsia-500/40 hover:border-fuchsia-400',
    sky: 'bg-sky-500/20 border-sky-500/40 hover:border-sky-400',
    green: 'bg-green-500/20 border-green-500/40 hover:border-green-400',
    gray: 'bg-gray-500/20 border-gray-500/40 hover:border-gray-400'
  };

  const textColorClasses = {
    blue: 'text-blue-400',
    cyan: 'text-cyan-400',
    teal: 'text-teal-400',
    orange: 'text-orange-400',
    red: 'text-red-400',
    purple: 'text-purple-400',
    indigo: 'text-indigo-400',
    emerald: 'text-emerald-400',
    violet: 'text-violet-400',
    amber: 'text-amber-400',
    rose: 'text-rose-400',
    pink: 'text-pink-400',
    lime: 'text-lime-400',
    yellow: 'text-yellow-400',
    fuchsia: 'text-fuchsia-400',
    sky: 'text-sky-400',
    green: 'text-green-400',
    gray: 'text-gray-400'
  };

  const CategoryIcon = getCategoryIcon(category);

  // Tooltip content avec description
  const tooltipContent = module.description || `Outil ${module.name} pour le pentest.`;

  return (
    <div className="relative group">
      <div
        draggable={!disabled}
        onDragStart={(e) => onDragStart(e, module, category)}
        onClick={() => !disabled && onClick && onClick(module, category)}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`
          p-2 rounded-lg border cursor-grab active:cursor-grabbing
          transition-all duration-200
          ${colorClasses[color] || colorClasses.gray}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <div className="flex items-center gap-2">
          <GripVertical className="w-3 h-3 text-gray-500" />
          <CategoryIcon className={`w-4 h-4 ${textColorClasses[color] || textColorClasses.gray}`} />
          <span className="text-xs font-medium text-white truncate flex-1">{module.name}</span>
          {module.installed && (
            <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
          )}
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && tooltipContent && (
        <div className="absolute z-50 bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg shadow-xl text-xs whitespace-normal min-w-[220px] max-w-[300px] pointer-events-none">
          <div className="font-semibold text-white mb-1">{module.name}</div>
          <div className="text-gray-300">{tooltipContent}</div>
          {module.installed ? (
            <div className="mt-1 text-green-400 text-[10px] flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Installed
            </div>
          ) : (
            <div className="mt-1 text-orange-400 text-[10px] flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Not installed
            </div>
          )}
          <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-600"></div>
        </div>
      )}
    </div>
  );
};

// Component for a module in the attack chain
const ChainModule = ({ module, index, onRemove, status }) => {
  const statusIcons = {
    pending: <div className="w-3 h-3 rounded-full bg-gray-500" />,
    running: <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />,
    completed: <CheckCircle className="w-4 h-4 text-green-400" />,
    failed: <AlertTriangle className="w-4 h-4 text-red-400" />,
  };

  const color = getCategoryColor(module.category);
  const borderColors = {
    blue: 'border-blue-500/50',
    cyan: 'border-cyan-500/50',
    orange: 'border-orange-500/50',
    red: 'border-red-500/50',
    purple: 'border-purple-500/50',
    green: 'border-green-500/50',
    yellow: 'border-yellow-500/50',
    gray: 'border-gray-500/50'
  };

  const CategoryIcon = getCategoryIcon(module.category);

  return (
    <div className="flex items-center gap-2">
      {index > 0 && <ArrowRight className="w-4 h-4 text-gray-500" />}
      <div className={`
        flex items-center gap-3 px-4 py-3 rounded-lg
        bg-gray-800/80 border ${borderColors[color] || borderColors.gray}
        transition-all duration-200
      `}>
        <CategoryIcon className="w-5 h-5 text-gray-400" />
        <div className="flex-1">
          <p className="text-sm font-medium text-white">{module.name}</p>
          <p className="text-xs text-gray-500">{module.tool || module.id}</p>
        </div>
        <div className="flex items-center gap-2">
          {statusIcons[status || 'pending']}
          <button
            onClick={() => onRemove(index)}
            className="p-1 text-gray-500 hover:text-red-400 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant principal AttackBuilder
const AttackBuilder = () => {
  const { t } = useI18n();

  // States
  const [targets, setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [manualTarget, setManualTarget] = useState('');
  const [targetMode, setTargetMode] = useState('discovered'); // 'discovered' or 'manual'
  const [attackChain, setAttackChain] = useState([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionStatus, setExecutionStatus] = useState({});
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [scansData, setScansData] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);

  // New states for tools from API
  const [allTools, setAllTools] = useState({});
  const [toolsByCategory, setToolsByCategory] = useState({});
  const [loadingTools, setLoadingTools] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // All categories collapsed by default
  const [expandedCategories, setExpandedCategories] = useState({});

  // State for predefined AI attack chains
  const [aiPredefinedChains] = useState([
    { id: 'quick_silent', name: 'Rapide & Silencieux', icon: 'Clock', color: 'green', modules: ['nmap', 'whatweb'], description: 'Scan discret et rapide' },
    { id: 'quick_aggressive', name: 'Rapide & Agressif', icon: 'Zap', color: 'orange', modules: ['masscan', 'nuclei'], description: 'Scan rapide et bruyant' },
    { id: 'deep_silent', name: 'Complet & Silencieux', icon: 'Search', color: 'blue', modules: ['nmap', 'nikto', 'nuclei', 'gobuster'], description: 'Reconnaissance approfondie' },
    { id: 'deep_aggressive', name: 'Complet & Agressif', icon: 'Shield', color: 'red', modules: ['masscan', 'nuclei', 'sqlmap', 'hydra'], description: 'Pentest complet' },
  ]);
  const [aiLiveLog, setAiLiveLog] = useState([]);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [executionLogs, setExecutionLogs] = useState([]);

  const dropZoneRef = useRef(null);

  // Fetch all tools from API
  useEffect(() => {
    const fetchTools = async () => {
      setLoadingTools(true);
      try {
        const response = await apiClient.get('/system/tools');
        const data = response.data;
        const tools = data.tools || {};
        setAllTools(tools);

        // Group by category
        const grouped = {};
        Object.entries(tools).forEach(([toolId, tool]) => {
          const cat = tool.category || 'other';
          if (!grouped[cat]) {
            grouped[cat] = [];
          }
          grouped[cat].push({
            id: toolId,
            name: tool.name,
            description: tool.description,
            tool: toolId,
            installed: tool.installed,
            enabled_for_ai: tool.enabled_for_ai,
            category: cat
          });
        });
        setToolsByCategory(grouped);
      } catch (error) {
        console.error('Error fetching tools:', error);
      } finally {
        setLoadingTools(false);
      }
    };
    fetchTools();
  }, []);

  // Load scans and discovered hosts
  useEffect(() => {
    const fetchTargets = async () => {
      setLoadingTargets(true);
      try {
        const response = await scans.list(0, 100);
        const scansList = response.scans || response || [];
        setScansData(scansList);

        // Extract hosts from all scans
        const allTargets = [];
        for (const scan of scansList) {
          if (scan.id) {
            try {
              const hostsData = await scans.getHosts(scan.id);
              const hosts = hostsData.hosts || hostsData || [];
              hosts.forEach(host => {
                allTargets.push({
                  id: host.id,
                  ip: host.ip_address,
                  hostname: host.hostname,
                  os: host.os_name ?? host.os ?? 'Inconnu',
                  scanId: scan.id,
                  scanName: scan.name || scan.target,
                  ports: host.open_ports_count ?? host.ports_count ?? 0,
                  vulns: host.vulnerabilities_count ?? host.vulns_count ?? 0,
                  services: host.services || []
                });
              });
            } catch (err) {
              console.warn(`Could not fetch hosts for scan ${scan.id}:`, err);
            }
          }
        }
        setTargets(allTargets);
      } catch (error) {
        console.error('Error fetching targets:', error);
      } finally {
        setLoadingTargets(false);
      }
    };

    fetchTargets();
  }, []);

  // Charger les workspaces pour la selection client
  useEffect(() => {
    const fetchWorkspaces = async () => {
      setLoadingWorkspaces(true);
      try {
        const response = await workspacesApi.list(0, 100);
        // L'API retourne directement un array, pas un objet avec .workspaces
        const workspacesList = Array.isArray(response) ? response : (response.workspaces || response.data || []);
        setWorkspaces(workspacesList);
      } catch (error) {
        console.error('Failed to fetch workspaces:', error);
        setWorkspaces([]);
      } finally {
        setLoadingWorkspaces(false);
      }
    };
    fetchWorkspaces();
  }, []);

  // Get active target (either selected or manual)
  const getActiveTarget = useCallback(() => {
    if (targetMode === 'manual' && manualTarget.trim()) {
      return {
        id: 'manual',
        ip: manualTarget.trim(),
        hostname: manualTarget.includes('.') && !manualTarget.match(/^\d/) ? manualTarget.trim() : null,
        os: 'Inconnu',
        ports: 0,
        vulns: 0
      };
    }
    return selectedTarget;
  }, [targetMode, manualTarget, selectedTarget]);

  // Generate AI suggestions based on selected target
  const generateAiSuggestions = useCallback(async () => {
    const target = getActiveTarget();
    if (!target) return;

    setAiLoading(true);
    try {
      const suggestions = [];

      if (target.ports > 0) {
        suggestions.push({
          id: 'suggest_1',
          title: 'Full reconnaissance',
          description: `${target.ports} open ports detected. Recommended: deep Nmap scan followed by Nikto.`,
          modules: ['nmap', 'nikto'],
          priority: 'high',
          reasoning: 'Establish a complete service map before exploitation'
        });
      }

      if (target.vulns > 0) {
        suggestions.push({
          id: 'suggest_2',
          title: 'Exploitation automatique',
          description: `${target.vulns} known vulnerabilities. Nuclei can verify them.`,
          modules: ['nuclei', 'metasploit'],
          priority: 'critical',
          reasoning: 'Existing vulnerabilities = immediate exploitation opportunities'
        });
      }

      if (suggestions.length === 0) {
        suggestions.push({
          id: 'suggest_default',
          title: 'Recommended baseline chain',
          description: 'Reconnaissance → Enumeration → Vulnerability testing',
          modules: ['nmap', 'nikto', 'nuclei'],
          priority: 'medium',
          reasoning: 'Methodical approach for unknown target'
        });
      }

      suggestions.push({
        id: 'suggest_brute',
        title: 'Default credentials test',
        description: 'Test default passwords on SSH/FTP/HTTP',
        modules: ['hydra'],
        priority: 'medium',
        reasoning: 'Often overlooked but very effective attack vector'
      });

      setAiSuggestions(suggestions);
    } catch (error) {
      console.error('Error generating AI suggestions:', error);
    } finally {
      setAiLoading(false);
    }
  }, [getActiveTarget]);

  // Regenerate suggestions when target changes
  useEffect(() => {
    const target = getActiveTarget();
    if (target) {
      generateAiSuggestions();
    } else {
      setAiSuggestions([]);
    }
  }, [selectedTarget, manualTarget, targetMode, generateAiSuggestions, getActiveTarget]);

  // Filter tools based on search query
  const filteredToolsByCategory = useCallback(() => {
    if (!searchQuery.trim()) {
      return toolsByCategory;
    }

    const query = searchQuery.toLowerCase();
    const filtered = {};

    Object.entries(toolsByCategory).forEach(([cat, tools]) => {
      const matchingTools = tools.filter(tool =>
        tool.name.toLowerCase().includes(query) ||
        tool.description?.toLowerCase().includes(query) ||
        tool.id.toLowerCase().includes(query)
      );
      if (matchingTools.length > 0) {
        filtered[cat] = matchingTools;
      }
    });

    return filtered;
  }, [toolsByCategory, searchQuery]);

  // Gestion du drag & drop
  const handleDragStart = (e, module, category) => {
    e.dataTransfer.setData('module', JSON.stringify({ ...module, category }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.add('ring-2', 'ring-red-500');
    }
  };

  const handleDragLeave = () => {
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove('ring-2', 'ring-red-500');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove('ring-2', 'ring-red-500');
    }

    try {
      const moduleData = JSON.parse(e.dataTransfer.getData('module'));
      setAttackChain(prev => [...prev, moduleData]);
    } catch (error) {
      console.error('Error dropping module:', error);
    }
  };

  // Add module on click
  const handleModuleClick = (module, category) => {
    setAttackChain(prev => [...prev, { ...module, category }]);
  };

  // Remove a module from the chain
  const removeFromChain = (index) => {
    setAttackChain(prev => prev.filter((_, i) => i !== index));
    setExecutionStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[index];
      return newStatus;
    });
  };

  // Appliquer une suggestion IA
  const applySuggestion = (suggestion) => {
    const modules = suggestion.modules.map(toolId => {
      const tool = allTools[toolId];
      if (tool) {
        return {
          id: toolId,
          name: tool.name,
          description: tool.description,
          tool: toolId,
          category: tool.category,
          installed: tool.installed
        };
      }
      return null;
    }).filter(Boolean);

    setAttackChain(prev => [...prev, ...modules]);
  };

  // Apply predefined AI chain
  const applyPredefinedChain = (chain) => {
    const modules = chain.modules.map(toolId => {
      const tool = allTools[toolId];
      if (tool) {
        return {
          id: toolId,
          name: tool.name,
          description: tool.description,
          tool: toolId,
          category: tool.category,
          installed: tool.installed
        };
      }
      return null;
    }).filter(Boolean);
    setAttackChain(modules);
  };

  // Add a real-time AI log
  const addAiLog = (message, type = 'analysis', icon = 'brain') => {
    setAiLiveLog(prev => [...prev, {
      message,
      type,
      icon,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  // Execute the attack chain
  const executeAttackChain = async () => {
    await executeAttackChainFrom(0, true);
  };

  // Execute the attack chain from an index
  const executeAttackChainFrom = async (startIndex = 0, resetLogs = false) => {
    const target = getActiveTarget();
    if (!target || attackChain.length === 0) return;

    setIsExecuting(true);
    if (resetLogs) {
      setExecutionLogs([]);
      setAiLiveLog([]);
    }

    const addLog = (message, type = 'info') => {
      setExecutionLogs(prev => [...prev, { message, type, timestamp: new Date().toLocaleTimeString() }]);
    };

    if (startIndex === 0) {
      addLog(`Starting attack chain on ${target.ip}`, 'info');
      addAiLog('Initialisation de l\'analyse IA...', 'thinking', 'brain');
      addAiLog(`Cible: ${target.ip} - Preparation de la reconnaissance`, 'observation', 'eye');
    } else {
      addLog(`Resuming attack chain from step ${startIndex + 1}`, 'info');
      addAiLog('Reprise de l\'analyse IA...', 'thinking', 'brain');
    }

    for (let i = startIndex; i < attackChain.length; i++) {
      const module = attackChain[i];
      setExecutionStatus(prev => ({ ...prev, [i]: 'running' }));
      addLog(`Execution: ${module.name} (${module.tool || module.id})...`, 'info');
      addAiLog(`In progress: ${module.name} - Real-time analysis...`, 'thinking', 'brain');

      try {
        await dynamicOrchestrator.executeTool(module.tool || module.id, target.ip);
        await new Promise(resolve => setTimeout(resolve, 1500));

        setExecutionStatus(prev => ({ ...prev, [i]: 'completed' }));
        addLog(`${module.name} completed successfully`, 'success');
        addAiLog(`${module.name} completed. Analyzing results...`, 'analysis', 'brain');
      } catch (error) {
        setExecutionStatus(prev => ({ ...prev, [i]: 'failed' }));
        addLog(`${module.name} failed: ${error.message}`, 'error');
        addAiLog(`Failure detected: ${module.name}. Analyzing bypass...`, 'warning', 'alert');
      }
    }

    addLog('Attack chain completed', 'success');
    addAiLog('Full analysis completed. Generating compromise report...', 'analysis', 'brain');
    setIsExecuting(false);
  };

  // Stop execution
  const stopExecution = async () => {
    try {
      await dynamicOrchestrator.stop();
      setIsExecuting(false);
      setExecutionLogs(prev => [...prev, {
        message: 'Execution stopped by user',
        type: 'warning',
        timestamp: new Date().toLocaleTimeString()
      }]);
    } catch (error) {
      console.error('Error stopping execution:', error);
    }
  };

  // Reset
  const resetBuilder = () => {
    setAttackChain([]);
    setExecutionStatus({});
    setExecutionLogs([]);
    setAiLiveLog([]);
  };

  // Toggle category
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const activeTarget = getActiveTarget();
  const filtered = filteredToolsByCategory();

  return (
    <div className="grid grid-cols-12 gap-6 h-[calc(100vh-200px)]">
      {/* Panel gauche - Workspace, Cible, Modules */}
      <div className="col-span-4 space-y-4 overflow-y-auto pr-2">

        {/* Selection du Workspace */}
        <div className="bg-gray-900/80 rounded-xl border border-gray-800 p-4">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-400" />
            Workspace
          </h3>
          {loadingWorkspaces ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
            </div>
          ) : (
            <>
              <select
                value={selectedWorkspaceId}
                onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option value="">-- Select a workspace --</option>
                {workspaces.map((ws) => (
                  <option key={ws.id} value={ws.id}>
                    {ws.company_name || ws.name} {ws.is_active === false && '(Inactif)'}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Associez cette attaque a un client pour les rapports
              </p>
            </>
          )}
        </div>

        {/* Target selection */}
        <div className="bg-gray-900/80 rounded-xl border border-gray-800 p-4">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Target className="w-5 h-5 text-red-400" />
            Cible
          </h3>

          {/* Mode selection tabs */}
          <div className="flex mb-3 bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setTargetMode('discovered')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                targetMode === 'discovered'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Discovered hosts
            </button>
            <button
              onClick={() => setTargetMode('manual')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                targetMode === 'manual'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Saisie manuelle
            </button>
          </div>

          {targetMode === 'discovered' ? (
            <>
              {loadingTargets ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-red-400 animate-spin" />
                </div>
              ) : targets.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Server className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No discovered hosts</p>
                  <p className="text-xs mt-1">Lancez un scan ou utilisez la saisie manuelle</p>
                </div>
              ) : (
                <select
                  value={selectedTarget?.id || ''}
                  onChange={(e) => {
                    const target = targets.find(t => t.id === e.target.value);
                    setSelectedTarget(target);
                  }}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-red-500 focus:ring-1 focus:ring-red-500"
                >
                  <option value="">Select a target...</option>
                  {targets.map(target => (
                    <option key={target.id} value={target.id}>
                      {target.ip} {target.hostname ? `(${target.hostname})` : ''} - {target.ports} ports
                    </option>
                  ))}
                </select>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <input
                type="text"
                value={manualTarget}
                onChange={(e) => setManualTarget(e.target.value)}
                placeholder="Domaine ou IP (ex: exemple.com, 192.168.1.1)"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-red-500 focus:ring-1 focus:ring-red-500"
              />
              <p className="text-xs text-gray-500">
                Enter a domain or public/private IP address
              </p>
            </div>
          )}

          {/* Target info display */}
          {activeTarget && (
            <div className="mt-3 p-3 bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Cible active</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  targetMode === 'manual' ? 'bg-orange-500/30 text-orange-300' : 'bg-green-500/30 text-green-300'
                }`}>
                  {targetMode === 'manual' ? 'Manual' : 'Discovered'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Adresse:</span>
                <span className="text-sm text-white font-mono">{activeTarget.ip}</span>
              </div>
              {activeTarget.hostname && (
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm text-gray-400">Hostname:</span>
                  <span className="text-sm text-white">{activeTarget.hostname}</span>
                </div>
              )}
              {targetMode === 'discovered' && (
                <>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-sm text-gray-400">Ports:</span>
                    <span className="text-sm text-green-400">{activeTarget.ports ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-sm text-gray-400">Vulns:</span>
                    <span className="text-sm text-red-400">{activeTarget.vulns ?? 0}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Modules disponibles avec recherche */}
        <div className="bg-gray-900/80 rounded-xl border border-gray-800 p-4">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-400" />
            Outils ({Object.values(allTools).length})
          </h3>

          {/* Search bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un outil..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-gray-400 focus:border-red-500 focus:ring-1 focus:ring-red-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <p className="text-xs text-gray-500 mb-4">
            Drag or click to add to the chain
          </p>

          {loadingTools ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {Object.entries(filtered).map(([categoryKey, tools]) => {
                const CategoryIcon = getCategoryIcon(categoryKey);
                const isExpanded = expandedCategories[categoryKey];
                const color = getCategoryColor(categoryKey);

                return (
                  <div key={categoryKey}>
                    <button
                      onClick={() => toggleCategory(categoryKey)}
                      className="w-full flex items-center justify-between p-2 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <CategoryIcon className={`w-4 h-4 text-${color}-400`} />
                        <span className="text-sm font-medium text-white">
                          {categoryNames[categoryKey] || categoryKey}
                        </span>
                        <span className="text-xs text-gray-500">({tools.length})</span>
                      </div>
                      <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>

                    {isExpanded && (
                      <div className="grid grid-cols-1 gap-1 mt-2 ml-2">
                        {tools.map(module => (
                          <DraggableModule
                            key={module.id}
                            module={module}
                            category={categoryKey}
                            onDragStart={handleDragStart}
                            onClick={handleModuleClick}
                            disabled={isExecuting}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {Object.keys(filtered).length === 0 && searchQuery && (
                <div className="text-center py-6 text-gray-500">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No tools found</p>
                  <p className="text-xs mt-1">Essayez un autre terme de recherche</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Panel central - Zone de drop + Suggestions IA */}
      <div className="col-span-5 space-y-4 overflow-y-auto">
        {/* Predefined AI attack chain buttons */}
        <div className="bg-gray-900/80 rounded-xl border border-green-500/30 p-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-green-400" />
            AI Attack Chains
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {aiPredefinedChains.map(chain => {
              const ChainIcon = ICON_MAP[chain.icon] || Zap;
              const colorClasses = {
                green: 'bg-green-600 hover:bg-green-500 border-green-500',
                orange: 'bg-orange-600 hover:bg-orange-500 border-orange-500',
                blue: 'bg-blue-600 hover:bg-blue-500 border-blue-500',
                red: 'bg-red-600 hover:bg-red-500 border-red-500'
              };
              return (
                <button
                  key={chain.id}
                  onClick={() => applyPredefinedChain(chain)}
                  disabled={isExecuting || !activeTarget}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg border text-white text-sm font-medium
                    transition-all disabled:opacity-50 disabled:cursor-not-allowed
                    ${colorClasses[chain.color]}
                  `}
                  title={chain.description}
                >
                  <ChainIcon className="w-4 h-4" />
                  {chain.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Zone de drop */}
        <div
          ref={dropZoneRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className="bg-gray-900/80 rounded-xl border-2 border-dashed border-gray-700 p-4 min-h-[200px] transition-all"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-red-400" />
              Attack chain
            </h3>
            {attackChain.length > 0 && (
              <button
                onClick={resetBuilder}
                className="text-xs text-gray-400 hover:text-red-400 flex items-center gap-1"
                disabled={isExecuting}
              >
                <Trash2 className="w-3 h-3" />
                Reset
              </button>
            )}
          </div>

          {attackChain.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Plus className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-sm">Glissez ou cliquez sur des modules</p>
              <p className="text-xs mt-1">to build your attack chain</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3 items-center">
              {attackChain.map((module, index) => (
                <ChainModule
                  key={`${module.id}-${index}`}
                  module={module}
                  index={index}
                  onRemove={removeFromChain}
                  status={executionStatus[index]}
                />
              ))}
            </div>
          )}
        </div>

        {/* Execution controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setExecutionStatus({});
              executeAttackChain();
            }}
            disabled={!activeTarget || attackChain.length === 0 || isExecuting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExecuting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('attack.executing')}
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                {t('attack.restartAll')}
              </>
            )}
          </button>

          <button
            onClick={() => {
              if (Object.keys(executionStatus).length === 0) {
                executeAttackChain();
              } else {
                const firstFailedIndex = attackChain.findIndex((_, i) =>
                  executionStatus[i] === 'failed' || !executionStatus[i]
                );
                if (firstFailedIndex >= 0) {
                  executeAttackChainFrom(firstFailedIndex);
                } else {
                  setExecutionStatus({});
                  executeAttackChain();
                }
              }
            }}
            disabled={!activeTarget || attackChain.length === 0 || isExecuting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Play className="w-5 h-5" />
            {Object.keys(executionStatus).length === 0 ? t('attack.launchChain') : t('attack.continueChain')}
          </button>

          {isExecuting && (
            <button
              onClick={stopExecution}
              className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
            >
              <Pause className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Suggestions IA */}
        <div className="bg-gray-900/80 rounded-xl border border-purple-500/30 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              {t('attack.aiSuggestions')}
            </h3>
            {activeTarget && (
              <button
                onClick={generateAiSuggestions}
                disabled={aiLoading}
                className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
              >
                <RefreshCw className={`w-3 h-3 ${aiLoading ? 'animate-spin' : ''}`} />
                Regenerate
              </button>
            )}
          </div>

          {!activeTarget ? (
            <div className="text-center py-6 text-gray-500">
              <Brain className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Select a target</p>
              <p className="text-xs mt-1">pour recevoir des suggestions IA</p>
            </div>
          ) : aiLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
              <span className="ml-2 text-gray-400">Analyse...</span>
            </div>
          ) : (
            <div className="space-y-3">
              {aiSuggestions.map(suggestion => (
                <div
                  key={suggestion.id}
                  className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 hover:border-purple-400/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-400" />
                        <h4 className="font-medium text-white">{suggestion.title}</h4>
                        <span className={`
                          text-xs px-2 py-0.5 rounded-full
                          ${suggestion.priority === 'critical' ? 'bg-red-500/30 text-red-300' :
                            suggestion.priority === 'high' ? 'bg-orange-500/30 text-orange-300' :
                            'bg-blue-500/30 text-blue-300'}
                        `}>
                          {suggestion.priority}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400 mt-1">{suggestion.description}</p>
                      <p className="text-xs text-purple-300 mt-2 italic">{suggestion.reasoning}</p>
                    </div>
                    <button
                      onClick={() => applySuggestion(suggestion)}
                      disabled={isExecuting}
                      className="ml-3 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs rounded-lg transition-colors disabled:opacity-50"
                    >
                      Appliquer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AI Live Monitoring */}
        <div className="bg-gray-900/80 rounded-xl border border-green-500/30 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-green-400" />
              IA Monitoring Live
              {aiAnalyzing && (
                <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded-full">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Analyse...
                </span>
              )}
            </h3>
            {aiLiveLog.length > 0 && (
              <button
                onClick={() => setAiLiveLog([])}
                className="text-xs text-gray-400 hover:text-red-400"
              >
                Effacer
              </button>
            )}
          </div>

          <div className="bg-black/40 rounded-lg p-3 max-h-[200px] overflow-y-auto">
            {aiLiveLog.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Brain className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">IA en attente</p>
              </div>
            ) : (
              <div className="space-y-2">
                {aiLiveLog.map((log, index) => {
                  const iconMap = {
                    brain: <Brain className="w-4 h-4 text-purple-400" />,
                    eye: <Eye className="w-4 h-4 text-cyan-400" />,
                    lightbulb: <Lightbulb className="w-4 h-4 text-yellow-400" />,
                    alert: <AlertTriangle className="w-4 h-4 text-red-400" />,
                    trending: <TrendingUp className="w-4 h-4 text-green-400" />,
                  };
                  const typeColors = {
                    thinking: 'text-gray-400 italic',
                    observation: 'text-cyan-300',
                    analysis: 'text-purple-300',
                    suggestion: 'text-yellow-300',
                    critical: 'text-red-300 font-semibold',
                    warning: 'text-orange-300',
                    success: 'text-green-300',
                  };

                  return (
                    <div key={index} className="flex items-start gap-2 text-sm">
                      <span className="text-gray-600 text-xs shrink-0">[{log.timestamp}]</span>
                      <span className="shrink-0">{iconMap[log.icon] || iconMap.brain}</span>
                      <span className={typeColors[log.type] || 'text-gray-300'}>
                        {log.message}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Panel droit - Console */}
      <div className="col-span-3 space-y-4">
        <div className="bg-gray-900/80 rounded-xl border border-gray-800 p-4 h-full flex flex-col">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Terminal className="w-5 h-5 text-green-400" />
            Console
          </h3>

          <div className="flex-1 bg-black/50 rounded-lg p-3 font-mono text-xs overflow-y-auto">
            {executionLogs.length === 0 ? (
              <div className="text-gray-600 text-center py-8">
                <Monitor className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Awaiting execution...</p>
              </div>
            ) : (
              <div className="space-y-1">
                {executionLogs.map((log, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="text-gray-600">[{log.timestamp}]</span>
                    <span className={`
                      ${log.type === 'success' ? 'text-green-400' :
                        log.type === 'error' ? 'text-red-400' :
                        log.type === 'warning' ? 'text-yellow-400' :
                        'text-gray-400'}
                    `}>
                      {log.message}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-gray-800">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Statut:</span>
              <span className={`font-medium ${isExecuting ? 'text-yellow-400' : 'text-gray-500'}`}>
                {isExecuting ? t('attack.inProgress') : t('attack.ready')}
              </span>
            </div>
            {attackChain.length > 0 && (
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-gray-400">Modules:</span>
                <span className="text-white">
                  {Object.values(executionStatus).filter(s => s === 'completed').length}/{attackChain.length}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttackBuilder;
